// Identity-aware presentation dedupe. Ordinary re-registration rows collapse
// by the backend's composite digest, with a conservative serial fallback for
// older responses. Independently credentialed conflict and clone-split rows
// always remain visible because each consumes its own endpoint quota slot.

const FLAG_KEY = "origin.feature.endpoint-dedupe";

// Serial values that are not unique machine identifiers — vendors ship
// these as BIOS/UEFI placeholders, and VMs/containers often report
// nothing. Compared case-insensitively against the trimmed serial.
const PLACEHOLDER_SERIALS = new Set([
    "",
    "0",
    "none",
    "n/a",
    "na",
    "null",
    "unknown",
    "invalid",
    "not specified",
    "not applicable",
    "default string",
    "system serial number",
    "to be filled by o.e.m.",
    "to be filled by o.e.m",
    "o.e.m.",
    "oem",
]);

// Normalized grouping key for a serial, or "" when the serial is missing
// or a known placeholder (meaning: do not group this row with anything).
function serialKey(serial) {
    const norm = String(serial ?? "").trim().toLowerCase();
    return PLACEHOLDER_SERIALS.has(norm) ? "" : norm;
}

function endpointSerial(row) {
    return row?.serial ?? row?.serialNumber ?? row?.serial_number;
}

function endpointIdentityInfo(row) {
    return row?.identityInfo ?? row?.identity_info ?? {};
}

export function endpointIdentityReason(row) {
    const raw = row?.identityCreationReason ?? row?.identity_creation_reason ??
        endpointIdentityInfo(row)?.creationReason ?? endpointIdentityInfo(row)?.creation_reason;
    if (raw === 1) return "REGISTRATION";
    if (raw === 2) return "IDENTITY_CONFLICT";
    if (raw === 3) return "CLONE_SPLIT";
    return String(raw ?? "").trim().toUpperCase().replace(/^ENDPOINT_IDENTITY_CREATION_REASON_/, "");
}

export function endpointIdentityBadge(row) {
    switch (endpointIdentityReason(row)) {
        case "CLONE_SPLIT":
            return {
                label: "Clone split",
                title: "Created after Origin detected two live copies using the same endpoint identity.",
            };
        case "IDENTITY_CONFLICT":
            return {
                label: "Identity conflict",
                title: "Registered separately because a matching endpoint could not be safely reclaimed.",
            };
        default:
            return null;
    }
}

function digestKey(row) {
    const raw = row?.deviceFingerprintV1Sha256 ?? row?.device_fingerprint_v1_sha256 ??
        endpointIdentityInfo(row)?.deviceFingerprintV1Sha256 ??
        endpointIdentityInfo(row)?.device_fingerprint_v1_sha256;
    let bytes;
    if (typeof raw === "string") {
        if (!raw) return "";
        if (/^[0-9a-f]{64}$/i.test(raw)) return raw.toLowerCase();
        try {
            const binary = atob(raw);
            bytes = Array.from(binary, (char) => char.charCodeAt(0));
        } catch {
            return "";
        }
    } else if (Array.isArray(raw) || ArrayBuffer.isView(raw)) {
        bytes = Array.from(raw);
    } else {
        return "";
    }
    if (bytes.length !== 32) return "";
    return bytes.map((byte) => Number(byte).toString(16).padStart(2, "0")).join("");
}

// Only an explicitly ordinary registration may participate in digest dedupe.
// Older rows with neither provenance nor a digest keep the serial fallback,
// but a digest-bearing row with missing provenance is kept separate. That
// conservative case protects clone/conflict rows while identity enrichment is
// unavailable instead of silently collapsing a quota-consuming endpoint.
function identityDedupeProtected(row) {
    const reason = endpointIdentityReason(row);
    if (reason === "REGISTRATION") return false;
    return Boolean(reason) || Boolean(digestKey(row));
}

function endpointId(row, index) {
    return row?.id ?? row?.endpointId ?? row?.endpoint_id ?? `row-${index}`;
}

// Epoch millis for a row's recency, or -Infinity when it has no snapshot
// (so any dated row beats an undated one for the same serial).
function snapshotMillis(row) {
    const t = row?.snapshotTime ?? row?.snapshot_time ?? row?.updatedAt ?? row?.updated_at;
    const ms = t instanceof Date ? t.getTime() : Date.parse(t);
    return Number.isFinite(ms) ? ms : -Infinity;
}

// Pure: collapse ordinary rows recognized as the same device, keeping the
// newest snapshot. Protected provenance rows always receive unique keys.
export function dedupeEndpointsBySerial(rows) {
    if (!Array.isArray(rows)) return [];

    const ordinaryDigestsBySerial = new Map();
    rows.forEach((row) => {
        if (identityDedupeProtected(row)) return;
        const serial = serialKey(endpointSerial(row));
        const digest = digestKey(row);
        if (!serial || !digest) return;
        if (!ordinaryDigestsBySerial.has(serial)) ordinaryDigestsBySerial.set(serial, new Set());
        ordinaryDigestsBySerial.get(serial).add(digest);
    });

    const groupKey = (row, index) => {
        if (identityDedupeProtected(row)) return `protected:${endpointId(row, index)}:${index}`;
        const digest = digestKey(row);
        if (digest) return `digest:${digest}`;
        const serial = serialKey(endpointSerial(row));
        if (!serial) return `unique:${endpointId(row, index)}:${index}`;
        const digests = ordinaryDigestsBySerial.get(serial);
        if (digests?.size === 1) return `digest:${digests.values().next().value}`;
        return `legacy:${serial}`;
    };

    const winnerIndexByKey = new Map();
    rows.forEach((row, i) => {
        const key = groupKey(row, i);
        const prev = winnerIndexByKey.get(key);
        if (prev === undefined || snapshotMillis(row) > snapshotMillis(rows[prev])) {
            winnerIndexByKey.set(key, i);
        }
    });

    const winners = new Set(winnerIndexByKey.values());
    return rows.filter((_row, i) => winners.has(i));
}

// How many rows the dedupe collapsed out of a loaded set.
//
// Both inventory surfaces count "shown" AFTER the dedupe while their totals and
// remaining-page counts are raw SERVER rows, so on a fleet of re-registered
// machines the two figures can never be reconciled — "64 shown / 389 reporting
// metadata" reads as though 325 machines are missing, and exhausting the pager
// lands far below the total with nothing on screen explaining the gap.
// Disclosing the merge is what makes the arithmetic add up. Keep the wording
// next to the grouping policy so every inventory surface reports it uniformly.
export function endpointDedupeMergedCount(fetchedCount, shownCount) {
    const merged = Number(fetchedCount) - Number(shownCount);
    return Number.isFinite(merged) && merged > 0 ? merged : 0;
}

// Default-ON kill switch. Disable in-browser without a redeploy:
//   localStorage.setItem("origin.feature.endpoint-dedupe", "0"); location.reload();
export function endpointDedupeEnabled() {
    try {
        if (typeof localStorage === "undefined") return true;
        const v = String(localStorage.getItem(FLAG_KEY) ?? "").trim().toLowerCase();
        return !(v === "0" || v === "false" || v === "no" || v === "off");
    } catch {
        return true;
    }
}

// Call-site entry point: dedupe when enabled, otherwise return the rows
// untouched (same reference). Display surfaces import only this symbol.
export function applyEndpointDedupe(rows) {
    return endpointDedupeEnabled() ? dedupeEndpointsBySerial(rows) : rows;
}
